import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  CheckCircle,
  XCircle,
  ChevronRight,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { http } from '@/network/http';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { useDeactivateTreatmentType } from '@/features/reports/hooks';
import { CreateTreatmentTypeModal } from './CreateTreatmentTypeModal';
import { TreatmentTypeDetailModal } from './TreatmentTypeDetailModal';
import toast from 'react-hot-toast';
import { useModal } from '@/components/modal';
import {
  ADMIN_COLORS,
  staggerContainer,
  staggerItem,
  triggerHapticFeedback,
} from '../utils/adminAnimations';
import { GlassCard } from '@/components/glass';
import { localizedName } from '@/utils/localized';
import type { TreatmentTypeDefinition, TreatmentTypeListResponse } from '@/features/reports/types';
import {
  PageHeader,
  StatCard,
  CardSkeleton,
  EmptyState,
  ErrorState,
  SearchInput,
  GlassSelect,
  CreateButton,
  BulkActionBar,
  StatusDot,
  InfiniteScrollSentinel,
} from '@/components/ui';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useRTL } from '@/hooks/useRTL';
import { useSelectionSet } from '@/hooks/useSelectionSet';

const PAGE_SIZE = 20;

const STATUS_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  ACTIVE: { bg: '#ecfdf5', border: '#047857', text: '#047857' },
  INACTIVE: { bg: '#f3f4f6', border: '#9ca3af', text: '#9ca3af' },
};

// ═══════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════
export function TreatmentsManagementPage() {
  const { t, i18n } = useTranslation();
  const { openConfirm } = useModal();
  const isRTL = useRTL();

  const { hasPermission } = usePermissions();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<TreatmentTypeDefinition | null>(null);

  const queryClient = useQueryClient();

  const deleteMutation = useDeactivateTreatmentType();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery({
    queryKey: ['treatmentTypes', 'infinite', search, statusFilter],
    queryFn: async ({ pageParam }) => {
      const params: Record<string, unknown> = {
        page: pageParam,
        limit: PAGE_SIZE,
      };
      if (search) params.search = search;
      if (statusFilter) params.isActive = statusFilter;

      const { data } = await http.get<TreatmentTypeListResponse>('/treatment-types', { params });
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.treatmentTypes.length, 0);
      return loaded < lastPage.pagination.total ? allPages.length + 1 : undefined;
    },
  });

  const sentinelRef = useInfiniteScroll({ fetchNextPage, hasNextPage, isFetchingNextPage });

  const allTypes = useMemo(
    () => data?.pages.flatMap((page) => page.treatmentTypes) || [],
    [data]
  );

  const { selectedIds, isSelected, toggle, selectAll, clear, count, allSelected } = useSelectionSet(allTypes);

  const totalCount = data?.pages[0]?.pagination.total || 0;
  const activeCount = useMemo(
    () => allTypes.filter((tt) => tt.isActive).length,
    [allTypes]
  );
  const inactiveCount = useMemo(
    () => allTypes.filter((tt) => !tt.isActive).length,
    [allTypes]
  );
  const totalReports = useMemo(
    () => allTypes.reduce((sum, tt) => sum + (tt.reportCount ?? 0), 0),
    [allTypes]
  );

  const handleBulkDelete = () => {
    triggerHapticFeedback();
    openConfirm({
      title: t('treatmentsAdmin.bulkDeleteTitle'),
      message: t('treatmentsAdmin.bulkDeleteMessage', { count }),
      variant: 'danger',
      confirmLabel: t('common.delete'),
      onConfirm: async () => {
        await Promise.all(Array.from(selectedIds).map(id => deleteMutation.mutateAsync(id)));
        toast.success(t('treatmentsAdmin.bulkDeletedSuccess'));
        clear();
      },
    });
  };

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="space-y-4 md:space-y-6 pb-24"
    >
      {/* ─── Header ──────────────────────────────────────────── */}
      <PageHeader
        icon={<FileText className="w-7 h-7" />}
        title={t('treatmentsAdmin.management')}
        action={
          hasPermission('action:manage_treatments') ? (
            <CreateButton
              label={t('treatmentsAdmin.addType')}
              onClick={() => setCreateModalOpen(true)}
            />
          ) : undefined
        }
      />

      {/* ─── Stats Cards ─────────────────────────────────────── */}
      <motion.div variants={staggerItem} className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon={<FileText className="w-7 h-7 md:w-8 md:h-8" />}
          label={t('treatmentsAdmin.totalTypes')}
          value={totalCount}
        />
        <StatCard
          icon={<CheckCircle className="w-7 h-7 md:w-8 md:h-8" />}
          label={t('treatmentsAdmin.active')}
          value={activeCount}
        />
        <StatCard
          icon={<XCircle className="w-7 h-7 md:w-8 md:h-8" />}
          label={t('treatmentsAdmin.inactive')}
          value={inactiveCount}
        />
        <StatCard
          icon={<FileText className="w-7 h-7 md:w-8 md:h-8" />}
          label={t('treatmentsAdmin.totalReports')}
          value={totalReports}
        />
      </motion.div>

      {/* ─── Search & Filters ────────────────────────────────── */}
      <motion.div variants={staggerItem}>
        <GlassCard title={t('common.filters')}>
        <div className="flex flex-col md:flex-row gap-3">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t('treatmentsAdmin.searchPlaceholder')}
          />

          <button
            onClick={selectAll}
            className="flex items-center gap-2 px-3 py-2 border border-white/30 rounded-lg bg-white/20 cursor-pointer hover:bg-white/20 transition-colors"
          >
            <input
              type="checkbox"
              checked={allSelected}
              onChange={selectAll}
              className="w-5 h-5 pointer-events-none"
              readOnly
            />
            <span className="font-bold text-sm">{t('treatmentsAdmin.selectAll')}</span>
          </button>

          <GlassSelect
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              triggerHapticFeedback();
            }}
          >
            <option value="">{t('treatmentsAdmin.allStatuses')}</option>
            <option value="true">{t('treatmentsAdmin.active')}</option>
            <option value="false">{t('treatmentsAdmin.inactive')}</option>
          </GlassSelect>
        </div>
        </GlassCard>
      </motion.div>

      {/* ─── Treatment Type List ──────────────────────────────── */}
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="skeleton"
            className="space-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </motion.div>
        ) : isError ? (
          <ErrorState message={t('treatmentsAdmin.loadError')} />
        ) : allTypes.length === 0 ? (
          <EmptyState
            icon={<FileText className="w-14 h-14 md:w-16 md:h-16" />}
            message={t('treatmentsAdmin.noTypes')}
          />
        ) : (
          <motion.div
            key="content"
            className="space-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {allTypes.map((tt) => {
              const selected = isSelected(tt.id);

              return (
                <motion.div
                  key={tt.id}
                  role="button"
                  tabIndex={0}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    triggerHapticFeedback();
                    setSelectedType(tt);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      triggerHapticFeedback();
                      setSelectedType(tt);
                    }
                  }}
                  className="flex items-center justify-between w-full py-1.5 ps-6 pe-4 md:py-2 md:ps-6 md:pe-4 rounded-xl text-start cursor-pointer border border-white/30 glass-surface"
                  style={{
                    boxShadow: selected
                      ? ADMIN_COLORS.shadowMd
                      : undefined,
                  }}
                >
                  {/* Left section */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {/* Checkbox — large tap target */}
                    <div
                      className="flex-shrink-0 flex items-center justify-center cursor-pointer"
                      style={{ minWidth: '44px', minHeight: '44px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggle(tt.id);
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggle(tt.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-5 h-5 pointer-events-none"
                      />
                    </div>

                    {/* Color dot icon circle */}
                    <div
                      className="w-10 h-10 md:w-12 md:h-12 rounded-full flex-shrink-0 flex items-center justify-center"
                      style={{
                        backgroundColor: tt.color || ADMIN_COLORS.ice,
                        border: `1px solid ${ADMIN_COLORS.border}`,
                      }}
                    >
                      <FileText className="w-5 h-5 md:w-6 md:h-6" style={{ color: '#fff' }} />
                    </div>

                    {/* Text content */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-xs md:text-sm text-neutral-800 truncate max-w-[160px] md:max-w-none">
                        {localizedName(tt, i18n.language)}
                      </p>
                    </div>
                  </div>

                  {/* Right section */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {/* Report count (desktop only) */}
                    <div className="hidden md:flex items-center gap-1 text-sm font-medium text-slate-800/70">
                      <FileText className="w-4 h-4" />
                      {tt.reportCount ?? 0}
                    </div>

                    {/* Status dot */}
                    <StatusDot
                      active={tt.isActive}
                      activeColor={STATUS_COLORS.ACTIVE.border}
                      inactiveColor={STATUS_COLORS.INACTIVE.border}
                      title={tt.isActive ? t('treatmentsAdmin.active') : t('treatmentsAdmin.inactive')}
                    />

                    {/* Chevron */}
                    <ChevronRight
                      className="w-5 h-5 text-slate-500"
                      style={{ transform: isRTL ? 'scaleX(-1)' : undefined }}
                    />
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── Infinite scroll sentinel ─────────────────────────── */}
      <InfiniteScrollSentinel
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sentinelRef={sentinelRef}
      />

      {/* ─── Modals ───────────────────────────────────────────── */}
      <CreateTreatmentTypeModal
        isOpen={createModalOpen}
        onClose={() => { queryClient.refetchQueries({ queryKey: ['treatmentTypes'] }); setCreateModalOpen(false); }}
        onSuccess={() => {}}
      />

      {selectedType && (
        <TreatmentTypeDetailModal
          treatmentType={selectedType}
          onClose={() => { queryClient.refetchQueries({ queryKey: ['treatmentTypes'] }); setSelectedType(null); }}
        />
      )}

      {/* ─── Bulk Action Bar ───────────────────────────────────── */}
      <BulkActionBar
        count={count}
        onDelete={handleBulkDelete}
        onClear={clear}
        isDeleting={deleteMutation.isPending}
      />
    </motion.div>
  );
}
